import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { When } from 'react-if';
import { DndKit } from '../../../external/dnd-kit/dnd-kit';
import { ImmerFuncs } from '../../../external/immer/immer.funcs';
import { Luxon } from '../../../external/luxon/luxon';
import { NodeWire } from '../../../external/server/node.wire';
import { ServerApi } from '../../../external/server/server.api';
import { CssFuncs } from '../../../foundation/css/css.funcs';
import { ArrayFuncs } from '../../../foundation/js/array.funcs';
import { JsFuncs } from '../../../foundation/js/js.funcs';
import { StackY } from '../../../foundation/layout/stack-y';
import { EffectTypeWire } from '../../core/effect/effect-type.wire';
import { EffectWireFuncs } from '../../core/effect/effect.wire.func';
import { SnoozeEffectDataWire } from '../../core/effect/effects/snooze/snooze-effect-data.wire';
import { BasicNodeSubWireFuncs } from '../../core/node/basic-node-sub.wire.funcs';
import { NodeStore } from '../../core/node/node.store';
import { NodeItemSelf } from './node-item-self';
import { NodeItemStyle } from './node-item.style';

export function NodeItem({
  nodeId,
  parentId,
  indentationLevel,
  displaySubItemsOnly,
  disableTodayVisualHolding,
  displayNewSubItemPlaceholder,
  didCreate,
  onRemove,
  onDraggingOverChanged,
}: {
  nodeId?: string;
  parentId?: string;
  indentationLevel?: number;
  displaySubItemsOnly?: boolean;
  disableTodayVisualHolding?: boolean;
  displayNewSubItemPlaceholder?: boolean;
  didCreate?: (node: NodeWire) => void;
  onRemove?: (nodeId: string) => void;
  onDraggingOverChanged?: (isOver: boolean) => void;
}) {
  const node = NodeStore.useItem(nodeId);
  const [focused, setFocused] = useState(false);
  const [isDraggingOverChild, setIsDraggingOverChild] = useState(0);
  const theIndenttationLevel = displaySubItemsOnly ? -1 : (indentationLevel ?? 0);

  const handleDraggingOverChanged = useCallback((isOver: boolean) => {
    setIsDraggingOverChild((prev) => {
      const next = prev + (isOver ? 1 : -1);
      return next;
    });
  }, []);

  const draggable = useDraggable({
    id: nodeId!,
    data: {
      type: 'node',
      nodeId,
      parentId,
    },
  });

  /*
   * MARK - Add Sub Item
   */

  const handleDidCreate = useCallback(
    (createdNode: NodeWire) => {
      const sub = ImmerFuncs.produce(ArrayFuncs.ensure(node?.sub), (draft) => {
        BasicNodeSubWireFuncs.addItem(draft, { child: createdNode.id });
      });
      NodeStore.updateItem(nodeId!, { sub });
      ServerApi.updateNode(nodeId!, { sub });
    },
    [nodeId, node],
  );

  /*
   * MARK - Remove
   */

  const handleChildRemove = useCallback(
    (child: string) => {
      if (!node) {
        return;
      }

      const sub = ImmerFuncs.produce(ArrayFuncs.ensure(node?.sub), (draft) => {
        BasicNodeSubWireFuncs.removeItem(draft, { child });
      });

      NodeStore.updateItem(node.id, { sub });
      ServerApi.updateNode(node.id, { sub });
    },
    [node],
  );

  const effects = ArrayFuncs.ensure(node?.effects);
  const snoozeEffect = EffectWireFuncs.findEffect<SnoozeEffectDataWire>({ effects, type: EffectTypeWire.snooze });
  const hasSnoozeEffect = JsFuncs.isNotNull(snoozeEffect);
  const snoozeEffectIsActive = hasSnoozeEffect && Luxon.DateTime.now() < Luxon.DateTime.fromFormat(snoozeEffect!.data.date, 'yyyy-MM-dd') && !node.done;

  const isPlaceholder = !node;

  return (
    <NodeItemStyle.Container
      className={CssFuncs.className((names) => {
        names.push(nodeId ? 'is-not-placeholder' : 'is-placeholder');
        names.push(focused ? 'focused' : 'not-focused');
        names.push(node?.done || snoozeEffectIsActive ? 'done' : 'not-done');
        if (disableTodayVisualHolding) {
          names.push('disable-today-visual-holding');
        }
      })}
      ref={node && draggable.setNodeRef}
      {...(node && draggable.attributes)}
      style={node && { transform: DndKit.CSS.Transform.toString(draggable.transform) }}
    >
      <When condition={!displaySubItemsOnly}>
        <NodeItemStyle.SelfDndLineAnchor style={{ left: `${theIndenttationLevel * 24}px` }}>
          <DragAndDropSlot nodeId={nodeId} parentId={parentId!} onOverChanged={onDraggingOverChanged} />
        </NodeItemStyle.SelfDndLineAnchor>
      </When>
      <StackY $gap="2px">
        <When condition={!displaySubItemsOnly}>
          <NodeItemSelf
            nodeId={nodeId}
            parentId={parentId}
            onFocusChanged={setFocused}
            didCreate={didCreate}
            onRemove={onRemove}
            theIndenttationLevel={theIndenttationLevel}
            draggable={draggable}
            isDraggingOverChild={isDraggingOverChild}
          />
        </When>
        {ArrayFuncs.ensure(node?.sub).map((subNodeId, i) => {
          return (
            <NodeItem
              key={subNodeId}
              nodeId={subNodeId}
              parentId={nodeId}
              indentationLevel={theIndenttationLevel + 1}
              disableTodayVisualHolding={disableTodayVisualHolding}
              onRemove={handleChildRemove}
              onDraggingOverChanged={handleDraggingOverChanged}
            />
          );
        })}
        <When condition={displayNewSubItemPlaceholder}>
          <NodeItem key={node?.sub?.length} parentId={nodeId} didCreate={handleDidCreate} indentationLevel={theIndenttationLevel + 1} />
        </When>
        <When condition={!isPlaceholder}>
          <NodeItemStyle.NewChildDndLineAnchor
            style={{
              left: `${(theIndenttationLevel + 1) * 24}px`,
              bottom: displayNewSubItemPlaceholder ? `35px` : '-1px', // 35 = 34 + 2 - 1
            }}
          >
            <DragAndDropSlot parentId={nodeId!} onOverChanged={handleDraggingOverChanged} />
          </NodeItemStyle.NewChildDndLineAnchor>
        </When>
      </StackY>
    </NodeItemStyle.Container>
  );
}

function DragAndDropSlot({ parentId, nodeId, onOverChanged }: { parentId: string; nodeId?: string; onOverChanged?: (isOver: boolean) => void }) {
  const { isOver, setNodeRef } = useDroppable({
    id: parentId + (nodeId ? '-drop-at-' + nodeId : '-drop-at-last'),
    data: {
      type: 'node',
      nodeId,
      nodeText: NodeStore.getItem(nodeId)?.text,
      parentId,
      parentText: NodeStore.getItem(parentId)?.text,
    },
  });
  const lastIsOver = useRef<boolean | null>(null);

  useEffect(() => {
    if (JsFuncs.isNotNull(lastIsOver.current) && lastIsOver.current !== isOver) {
      onOverChanged?.(isOver);
    }
    lastIsOver.current = isOver;
  }, [isOver]);

  return (
    <NodeItemStyle.DndLine ref={setNodeRef} style={{ opacity: isOver ? 1 : 0 }}>
      <NodeItemStyle.DndLineEnd1 />
      <NodeItemStyle.DndLineEnd2 />
    </NodeItemStyle.DndLine>
  );
}
